<template>
  <v-container fluid pt-0>
    <v-row align="center" class="pb-2">
      <h1 class="headline">Request Performance</h1>
      <v-spacer></v-spacer>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" icon @click="refresh">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </template>
        <span>Refresh</span>
      </v-tooltip>
    </v-row>

    <!-- Requests are only kept for about a day (Request.clear_table runs on
         logout), so the presets stop at 24h. Anything longer would return a
         partial window that reads as a collapse in traffic. -->
    <v-card class="elevation-2 mb-4">
      <v-card-text class="py-3">
        <v-row align="center" dense>
          <v-col cols="12" md="5">
            <v-btn-toggle v-model="preset" mandatory dense color="primary">
              <v-btn
                v-for="p in presets"
                :key="p.minutes"
                :value="p.minutes"
                class="px-4"
              >
                {{ p.label }}
              </v-btn>
              <v-btn :value="0" class="px-4">Custom</v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="6" md="2" v-if="preset === 0">
            <v-text-field
              v-model="customFrom"
              type="datetime-local"
              label="From"
              outlined
              dense
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="6" md="2" v-if="preset === 0">
            <v-text-field
              v-model="customTo"
              type="datetime-local"
              label="To"
              outlined
              dense
              hide-details
            ></v-text-field>
          </v-col>
          <v-spacer v-if="preset !== 0"></v-spacer>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="domain"
              label="Domain"
              hint="Blank searches every tenant"
              persistent-hint
              outlined
              dense
              clearable
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-alert v-if="errorMessage" type="error" dense text class="mb-2">
      {{ errorMessage }}
    </v-alert>

    <v-row dense class="mb-2">
      <v-col v-for="card in summaryCards" :key="card.label" cols="6" md="2">
        <v-card class="elevation-2 fill-height" :class="cardClass(card)">
          <v-card-text class="py-3">
            <div class="caption text-uppercase" :class="mutedClass">{{ card.label }}</div>
            <div class="title">{{ card.value }}</div>
            <div class="caption" :class="mutedClass" v-if="card.hint">{{ card.hint }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12">
        <v-card class="elevation-2">
          <v-card-text class="pa-4">
            <div class="caption grey--text text-uppercase pb-3">
              Requests per {{ bucketMinutes }} min, with 95th percentile
            </div>
            <!-- Tall enough that the two y axes get room for their ticks. At 90
                 they overlapped into an unreadable stack. -->
            <div v-if="timelineChartData" style="height: 260px">
              <bar-chart
                :chart-data="timelineChartData"
                :options="chartOptions"
                :height="260"
              ></bar-chart>
            </div>
            <div v-else class="caption grey--text">No requests in this window.</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-tabs v-model="tab" class="mt-3">
      <v-tab>By Operation</v-tab>
      <v-tab>By User</v-tab>
      <v-tab>Requests</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <!-- Sorted by total time on the server, deliberately not by count. An
           operation called 4 times at 90s matters more than one called 900
           times at 0.2s, and sorting by count hides exactly that. -->
      <v-tab-item v-for="groupTab in [0, 1]" :key="groupTab">
        <v-data-table
          :items="groups"
          :headers="groupHeaders"
          :loading="$apollo.queries.breakdown.loading"
          :items-per-page="25"
          sort-by="totalTime"
          :sort-desc="true"
          class="elevation-1 mt-2"
          loading-text="Loading... Please wait"
        >
          <template v-slot:item.label="{ item }">
            <a href="#" @click.prevent="drillInto(item)">{{ item.label }}</a>
          </template>
          <template v-slot:item.shareOfTotalTime="{ item }">
            <div class="d-flex align-center">
              <v-progress-linear
                :value="item.shareOfTotalTime"
                height="6"
                color="primary"
                class="mr-2"
                style="max-width: 90px"
              ></v-progress-linear>
              <span class="caption">{{ item.shareOfTotalTime.toFixed(1) }}%</span>
            </div>
          </template>
          <template v-slot:item.totalTime="{ item }">{{ secs(item.totalTime) }}</template>
          <template v-slot:item.avgTime="{ item }">{{ secs(item.avgTime) }}</template>
          <template v-slot:item.p50="{ item }">{{ secs(item.p50) }}</template>
          <template v-slot:item.p95="{ item }">{{ secs(item.p95) }}</template>
          <template v-slot:item.maxTime="{ item }">{{ secs(item.maxTime) }}</template>
        </v-data-table>
      </v-tab-item>

      <v-tab-item>
        <!-- Same card + padding as the table below it, so the filter row does
             not run wider than the data it filters. -->
        <v-card class="elevation-2 mt-2">
          <v-card-text class="py-3">
            <v-row align="center" dense>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="listOperation"
                  label="Operation"
                  outlined
                  dense
                  clearable
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="2" class="pl-md-4">
                <v-switch
                  v-model="slowOnly"
                  :label="`Slow only (>= ${slowThreshold}s)`"
                  class="mt-0"
                  dense
                  hide-details
                ></v-switch>
              </v-col>
              <v-col cols="6" md="2">
                <v-switch
                  v-model="incompleteOnly"
                  label="Did not finish"
                  class="mt-0"
                  dense
                  hide-details
                ></v-switch>
              </v-col>
              <v-col cols="6" md="2">
                <v-select
                  v-model="rowLimit"
                  :items="[100, 500, 1000, 2000]"
                  label="Rows"
                  outlined
                  dense
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="6" md="3" class="text-right">
                <v-btn color="primary" :disabled="!requests.length" @click="exportCsv">
                  <v-icon left small>mdi-download</v-icon>Export CSV
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-data-table
          :items="requests"
          :headers="requestHeaders"
          :loading="$apollo.queries.requestList.loading"
          :items-per-page="25"
          class="elevation-1 mt-2"
          loading-text="Loading... Please wait"
          @click:row="openDetails"
        >
          <template v-slot:item.createdAt="{ item }">{{ stamp(item.createdAt) }}</template>
          <template v-slot:item.durationSeconds="{ item }">
            <span v-if="item.durationSeconds === null" class="orange--text">did not finish</span>
            <span v-else>{{ secs(item.durationSeconds) }}</span>
          </template>
        </v-data-table>
      </v-tab-item>
    </v-tabs-items>

    <v-navigation-drawer v-model="detailsOpen" right temporary fixed width="640">
      <v-card flat v-if="selected">
        <v-card-title class="subtitle-1">
          {{ selected.operationName || "(no operation name)" }}
          <v-spacer></v-spacer>
          <v-btn icon @click="detailsOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="caption grey--text">When</div>
          <div class="pb-2">{{ stamp(selected.createdAt) }}</div>
          <div class="caption grey--text">Duration</div>
          <div class="pb-2">
            {{ selected.durationSeconds === null ? "did not finish" : secs(selected.durationSeconds) }}
          </div>
          <div class="caption grey--text">User</div>
          <div class="pb-2">{{ selected.userFullName || selected.username || selected.userId }}</div>
          <div class="caption grey--text">Domain</div>
          <div class="pb-2">{{ selected.domain }}</div>
          <div class="caption grey--text">URL</div>
          <div class="pb-2" style="word-break: break-all">{{ selected.url }}</div>
          <div class="caption grey--text">Session</div>
          <div class="pb-2" style="word-break: break-all">{{ selected.session }}</div>
          <div class="caption grey--text">Params</div>
          <v-progress-linear
            v-if="$apollo.queries.selectedDetails.loading"
            indeterminate
          ></v-progress-linear>
          <pre class="caption pa-2 grey lighten-4" style="white-space: pre-wrap; word-break: break-all">{{
            selectedParams
          }}</pre>
        </v-card-text>
      </v-card>
    </v-navigation-drawer>
  </v-container>
</template>

<script>
import gql from "graphql-tag";
import BarChart from "../dashboard/charts/BarChart";

// Two separate queries on purpose. requestStats runs one SQL statement per
// section selected, so keeping summary+timeline apart from groups means
// switching between the Operation and User tabs does not re-run the summary.
const OVERVIEW_QUERY = gql`
  query requestOverview($from: DateWithTimeType!, $to: DateWithTimeType!, $domain: String, $bucketMinutes: Int, $slowThreshold: Float) {
    requestStats(from: $from, to: $to, domain: $domain, bucketMinutes: $bucketMinutes, slowThreshold: $slowThreshold) {
      summary {
        totalRequests
        totalTime
        avgTime
        p50
        p95
        maxTime
        slowCount
        incompleteCount
        distinctUsers
      }
      timeline {
        bucket
        count
        avgTime
        p95
        slowCount
      }
    }
  }
`;

const BREAKDOWN_QUERY = gql`
  query requestBreakdown($from: DateWithTimeType!, $to: DateWithTimeType!, $domain: String, $groupBy: RequestStatsGroupBy, $slowThreshold: Float) {
    requestStats(from: $from, to: $to, domain: $domain, groupBy: $groupBy, slowThreshold: $slowThreshold) {
      groups {
        key
        label
        count
        totalTime
        shareOfTotalTime
        avgTime
        p50
        p95
        maxTime
        slowCount
        incompleteCount
      }
    }
  }
`;

// params is deliberately absent here - it holds the whole request body and is
// what makes the requests table big. The drawer fetches it for one row.
const REQUESTS_QUERY = gql`
  query requestList($filter: RequestFilterType, $first: Int) {
    requests(filter: $filter, orderBy: { fieldName: "createdAt", direction: DESC }, first: $first) {
      totalCount
      edges {
        node {
          requestId
          createdAt
          operationName
          controller
          action
          domain
          url
          durationSeconds
          username
          userFullName
          userId
          session
        }
      }
    }
  }
`;

const DETAILS_QUERY = gql`
  query requestDetails($filter: RequestFilterType) {
    requests(filter: $filter, first: 1) {
      edges {
        node {
          requestId
          params
        }
      }
    }
  }
`;

export default {
  name: "request-performance",

  components: { BarChart },

  data() {
    return {
      preset: 180,
      presets: [
        { label: "15m", minutes: 15 },
        { label: "1h", minutes: 60 },
        { label: "3h", minutes: 180 },
        { label: "12h", minutes: 720 },
        { label: "24h", minutes: 1440 },
      ],
      customFrom: "",
      customTo: "",
      domain: "",
      slowThreshold: 2.0,
      tab: 0,
      listOperation: "",
      slowOnly: false,
      incompleteOnly: false,
      rowLimit: 500,
      detailsOpen: false,
      selected: null,
      errorMessage: "",
      overview: null,
      breakdown: null,
      requestList: null,
      selectedDetails: null,
      groupHeaders: [
        { text: "Name", value: "label" },
        { text: "Calls", value: "count" },
        { text: "Total time", value: "totalTime" },
        { text: "Share of time", value: "shareOfTotalTime" },
        { text: "Average", value: "avgTime" },
        { text: "Median", value: "p50" },
        { text: "p95", value: "p95" },
        { text: "Slowest", value: "maxTime" },
        { text: "Slow", value: "slowCount" },
        { text: "Unfinished", value: "incompleteCount" },
      ],
      requestHeaders: [
        { text: "When", value: "createdAt" },
        { text: "Operation", value: "operationName" },
        { text: "Duration", value: "durationSeconds" },
        { text: "User", value: "username" },
        { text: "Domain", value: "domain" },
        { text: "Id", value: "requestId" },
      ],
      // maxTicksLimit matters more than it looks: without it chart.js fits a
      // tick per gridline, and at this width the two axes collapse into an
      // unreadable stack of overlapping numbers.
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: true, position: "bottom" },
        tooltips: { mode: "index", intersect: false },
        scales: {
          xAxes: [
            {
              ticks: { maxTicksLimit: 12, autoSkip: true, maxRotation: 0 },
              gridLines: { display: false },
            },
          ],
          yAxes: [
            {
              id: "count",
              position: "left",
              scaleLabel: { display: true, labelString: "Requests" },
              ticks: { beginAtZero: true, maxTicksLimit: 6, precision: 0 },
            },
            {
              id: "secs",
              position: "right",
              scaleLabel: { display: true, labelString: "p95 (s)" },
              ticks: { beginAtZero: true, maxTicksLimit: 6 },
              gridLines: { display: false },
            },
          ],
        },
      },
    };
  },

  computed: {
    mutedClass() {
      return this.$vuetify.theme.dark
        ? "grey--text text--lighten-1"
        : "grey--text text--darken-1";
    },
    // The backend parses these with DateTime.iso8601, which is strict - it wants
    // the T and an explicit offset. datetime-local gives "2026-08-04T09:30" in
    // local time, and toISOString does the conversion to UTC for us.
    fromIso() {
      if (this.preset === 0) return this.toIsoUtc(this.customFrom);
      return new Date(Date.now() - this.preset * 60000).toISOString();
    },
    toIso() {
      if (this.preset === 0) return this.toIsoUtc(this.customTo);
      return new Date().toISOString();
    },
    windowMinutes() {
      if (!this.fromIso || !this.toIso) return this.preset || 180;
      return Math.max(1, (new Date(this.toIso) - new Date(this.fromIso)) / 60000);
    },
    bucketMinutes() {
      const minutes = this.windowMinutes;
      if (minutes <= 30) return 1;
      if (minutes <= 240) return 5;
      if (minutes <= 720) return 15;
      return 60;
    },
    rangeReady() {
      return !!(this.fromIso && this.toIso);
    },
    domainArg() {
      const value = (this.domain || "").trim();
      return value === "" ? null : value;
    },
    statsGroupBy() {
      return this.tab === 1 ? "USER" : "OPERATION";
    },
    summary() {
      return this.overview ? this.overview.summary : null;
    },
    groups() {
      return this.breakdown ? this.breakdown.groups : [];
    },
    requests() {
      if (!this.requestList) return [];
      return this.requestList.edges.map((edge) => edge.node);
    },
    summaryCards() {
      const s = this.summary;
      if (!s) return [];
      const share = s.totalRequests ? (s.slowCount / s.totalRequests) * 100 : 0;
      // The last two cards only take on a warning colour when they have
      // something in them. A red box reading 0 is noise, and it trains you to
      // stop looking at the one place worth looking.
      return [
        { label: "Requests", value: this.num(s.totalRequests), color: "blue" },
        { label: "Users", value: this.num(s.distinctUsers), color: "indigo" },
        // Average, median and p95 skip requests that never finished, because
        // those have no duration recorded at all.
        {
          label: "Average",
          value: this.secs(s.avgTime),
          hint: "completed only",
          color: "teal",
        },
        {
          label: "Median / p95",
          value: `${this.secs(s.p50)} / ${this.secs(s.p95)}`,
          color: "cyan",
        },
        {
          label: `Slow (>= ${this.slowThreshold}s)`,
          value: this.num(s.slowCount),
          hint: `${share.toFixed(1)}% of requests`,
          color: s.slowCount > 0 ? "amber" : "blue-grey",
        },
        {
          label: "Did not finish",
          value: this.num(s.incompleteCount),
          color: s.incompleteCount > 0 ? "deep-orange" : "blue-grey",
        },
      ];
    },
    timelineChartData() {
      const timeline = this.overview ? this.overview.timeline : [];
      if (!timeline || !timeline.length) return null;
      return {
        labels: timeline.map((b) => this.clock(b.bucket)),
        datasets: [
          {
            label: "Requests",
            backgroundColor: "#1976d2",
            data: timeline.map((b) => b.count),
            yAxisID: "count",
          },
          {
            label: "p95 (s)",
            type: "line",
            borderColor: "#e65100",
            backgroundColor: "transparent",
            pointRadius: 2,
            data: timeline.map((b) => (b.p95 === null ? 0 : Number(b.p95.toFixed(2)))),
            yAxisID: "secs",
          },
        ],
      };
    },
    listFilter() {
      const filter = { created_at_gte: this.fromIso, created_at_lte: this.toIso };
      if (this.domainArg) filter.domain_eq = this.domainArg;
      if (this.listOperation && this.listOperation.trim() !== "") {
        filter.operation_name_eq = this.listOperation.trim();
      }
      if (this.slowOnly) filter.time_gte = this.slowThreshold;
      if (this.incompleteOnly) filter.time_null_eq = true;
      return filter;
    },
    selectedParams() {
      if (!this.selectedDetails || !this.selectedDetails.edges.length) return "";
      const params = this.selectedDetails.edges[0].node.params;
      // Null means the backend withheld it - this account is not GlobeTrack
      // support. The menu entry and the data rule are enforced separately.
      return params === null ? "(not available for this account)" : params;
    },
  },

  methods: {
    // The app has a dark theme toggle, and a lighten-5 tint on a dark surface
    // is unreadable, so the shade is picked per theme rather than hardcoded.
    cardClass(card) {
      const colour = card.color || "blue-grey";
      return this.$vuetify.theme.dark ? `${colour} darken-4` : `${colour} lighten-5`;
    },
    toIsoUtc(localValue) {
      if (!localValue) return null;
      const parsed = new Date(localValue);
      return isNaN(parsed.getTime()) ? null : parsed.toISOString();
    },
    num(value) {
      return value === null || value === undefined ? "-" : Number(value).toLocaleString();
    },
    secs(value) {
      if (value === null || value === undefined) return "-";
      const n = Number(value);
      if (n >= 60) return `${Math.floor(n / 60)}m ${Math.round(n % 60)}s`;
      return `${n.toFixed(2)}s`;
    },
    stamp(iso) {
      if (!iso) return "";
      return new Date(iso).toLocaleString();
    },
    clock(iso) {
      if (!iso) return "";
      return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },
    drillInto(item) {
      if (this.tab === 1) {
        this.listOperation = "";
      } else {
        this.listOperation = item.key === "(none)" ? "" : item.key;
      }
      this.tab = 2;
    },
    openDetails(row) {
      this.selected = row;
      this.detailsOpen = true;
    },
    exportCsv() {
      const columns = [
        "requestId",
        "createdAt",
        "operationName",
        "controller",
        "action",
        "domain",
        "url",
        "durationSeconds",
        "username",
        "userFullName",
        "userId",
        "session",
      ];
      const escape = (value) => {
        if (value === null || value === undefined) return "";
        const text = String(value);
        return /[;"\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
      };
      const lines = [columns.join(";")];
      this.requests.forEach((row) => {
        lines.push(columns.map((column) => escape(row[column])).join(";"));
      });
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `requests_${this.fromIso}_${this.toIso}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    refresh() {
      this.errorMessage = "";
      this.$apollo.queries.overview.refetch();
      this.$apollo.queries.breakdown.refetch();
      if (this.tab === 2) this.$apollo.queries.requestList.refetch();
    },
    onError(error) {
      this.errorMessage = error && error.message ? error.message : String(error);
    },
  },

  apollo: {
    overview: {
      query: OVERVIEW_QUERY,
      fetchPolicy: "network-only",
      variables() {
        return {
          from: this.fromIso,
          to: this.toIso,
          domain: this.domainArg,
          bucketMinutes: this.bucketMinutes,
          slowThreshold: this.slowThreshold,
        };
      },
      skip() {
        return !this.rangeReady;
      },
      update: (data) => data.requestStats,
      error(error) {
        this.onError(error);
      },
    },
    breakdown: {
      query: BREAKDOWN_QUERY,
      fetchPolicy: "network-only",
      variables() {
        return {
          from: this.fromIso,
          to: this.toIso,
          domain: this.domainArg,
          groupBy: this.statsGroupBy,
          slowThreshold: this.slowThreshold,
        };
      },
      skip() {
        return !this.rangeReady || this.tab === 2;
      },
      update: (data) => data.requestStats,
      error(error) {
        this.onError(error);
      },
    },
    requestList: {
      query: REQUESTS_QUERY,
      fetchPolicy: "network-only",
      variables() {
        return { filter: this.listFilter, first: this.rowLimit };
      },
      skip() {
        return !this.rangeReady || this.tab !== 2;
      },
      update: (data) => data.requests,
      error(error) {
        this.onError(error);
      },
    },
    selectedDetails: {
      query: DETAILS_QUERY,
      fetchPolicy: "network-only",
      variables() {
        return {
          filter: {
            id_eq: this.selected ? this.selected.requestId : 0,
            created_at_gte: this.fromIso,
            created_at_lte: this.toIso,
          },
        };
      },
      skip() {
        return !this.detailsOpen || !this.selected;
      },
      update: (data) => data.requests,
      error(error) {
        this.onError(error);
      },
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/admin/RequestPerformance.vue